import type { OidcAccessToken } from '@ori/auth';
import { useProductInfoDialog } from '@ori/product-info-dialog-lib';
import { useCallback, useEffect, useState } from 'react';
import type { FunctionComponent } from 'react';

import { NOT_LOGGED_IN_USER_ID } from '../constants';
import { useConfiguration } from '../contexts';
import { useCloseEvents, useOpenEvent, useTranslations } from '../hooks';

export interface DialogProps {
  token: OidcAccessToken;
}

export const Dialog: FunctionComponent<DialogProps> = ({ token }) => {
  const [open, setOpen] = useState(false);
  const onClose = useCallback(() => setOpen(false), []);
  const onOpen = useCallback(() => setOpen(true), []);
  const customerId = token.getClaim('customer_id') ?? NOT_LOGGED_IN_USER_ID;
  const isAnonymousUser = customerId === NOT_LOGGED_IN_USER_ID;
  const { currentLanguage, globalApiUrl, graphqlUrl, staticApiUrl, tenant } = useConfiguration();
  const {
    atpClusterId,
    initialProductCode,
    lineNumber,
    mode,
    originalItemCode,
    originalQuantity,
    placement,
    position,
    productCodes,
    replaceSubscription,
  } = useOpenEvent({ onOpen });

  const { closeProductInfoDialog, openProductInfoDialog, productInfoDialog } = useProductInfoDialog(
    {
      atpClusterId,
      analytics: {
        lineNumber,
        position,
        placement,
      },
      currentLanguage,
      customer: {
        customerId,
        isAnonymousUser,
      },
      dialog: { onClose },
      globalApiUrl,
      graphQlUrl: graphqlUrl,
      initialProductCode,
      mode,
      productCodes,
      shared: {
        originalItemCode,
        originalQuantity,
      },
      staticApiUrl,
      tenant,
      token: token.toString(),
      translations: useTranslations(),
      replaceSubscription,
    },
  );
  useCloseEvents({ onClose });
  useEffect(() => {
    if (open) {
      openProductInfoDialog();
    } else {
      closeProductInfoDialog();
    }
  }, [closeProductInfoDialog, open, openProductInfoDialog]);

  return productInfoDialog;
};
